import { PASSWORD_MIN_LENGTH } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input } from 'antd';
import { Lock } from 'lucide-react';
import type { UserListItem } from './users.api';

interface ResetPasswordFormValues {
  password: string;
}

interface ResetPasswordDrawerProps {
  open: boolean;
  user: UserListItem | undefined;
  error: string | null;
  submitting: boolean;
  onSubmit: (password: string) => Promise<boolean>;
  onClose: () => void;
}

/** A temporary password the admin sets directly, same shape as CreateUserDrawer's initial password — the user must change it at their next sign-in. */
export function ResetPasswordDrawer({ open, user, error, submitting, onSubmit, onClose }: ResetPasswordDrawerProps) {
  const [form] = Form.useForm<ResetPasswordFormValues>();

  async function submit(values: ResetPasswordFormValues) {
    const succeeded = await onSubmit(values.password);
    if (succeeded) {
      form.resetFields();
      onClose();
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title={user ? `Reset password for ${user.name}` : 'Reset password'} placement="right" size={420} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<ResetPasswordFormValues> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <Form.Item
          name="password"
          label="Temporary password"
          rules={[{ required: true, min: PASSWORD_MIN_LENGTH, message: `Password must be at least ${PASSWORD_MIN_LENGTH} characters` }]}
        >
          <Input.Password prefix={<Lock className="size-4 text-muted" />} autoComplete="new-password" autoFocus />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            Reset password
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
